import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import classNames from 'classnames';
import { Button } from 'components';
import Tick from 'assets/icons/tick.svg?react';
import { useClientQuery, useActiveLoanQuery } from 'hooks/webapi';
import Card from 'components/Card/Card';
import styles from 'pages/Registration/Approved/Approved.module.scss';
import Typography from 'components/Typography/Typography';
import OfferTable from 'components/OfferTable/OfferTable';
import { useTranslation } from 'react-i18next';
import { postMessageToApp } from 'utils/postMessageToApp';
import { session, useSession } from 'session';

export default function Approved() {
  const { t } = useTranslation();
  const [pending, setPending] = useState(false);
  const { data: client, refetch: refetchClient } = useClientQuery();
  const { data: activeLoan } = useActiveLoanQuery();

  const isAuthModeResolving = useSession((state) => state.mode === null);

  useEffect(() => {
    if (activeLoan) {
      postMessageToApp('first_loan_approved', { activeLoan });
    }
  }, []);

  const handleGoToAccount = async () => {
    setPending(true);
    await session.handOverToDashboard();
  };

  useEffect(() => {
    async function fetchClientAsync() {
      try {
        await refetchClient();
      } catch {
        /* empty */
      }
    }
    if (!client) {
      fetchClientAsync();
    }
  }, [client, refetchClient]);

  return (
    <>
      <Helmet>
        <title>{t('approved:html_title')}</title>
      </Helmet>

      <div className={styles['box-wrapper']} data-test="approved-wrapepr">
        <Card className={styles.card} dataTest="approved-card">
          <div className={styles.check} data-test="approved-check">
            <Tick className={styles.icon} />
          </div>

          <Typography
            kind="h1"
            size="xl"
            className={styles.title}
            fontWeight={700}
            dataTest="approved-title"
          >
            {t('approved:title')}
          </Typography>

          <Typography
            className={styles.description}
            size="s"
            dataTest="approved-description"
          >
            {t('approved:description')}
          </Typography>

          <div className={styles.cell} data-test="approved-offer-table">
            <OfferTable
              totalPrincipal={activeLoan?.principalAmount}
              totalRepayableAmount={activeLoan?.totalInvoicedAmount}
              extensionFees={activeLoan?.loanExtensionFees[0].fee}
              interestRate={activeLoan?.interestRatePerYear}
              assignedInterest={activeLoan?.assignedInterest}
              firstPaymentDate={activeLoan?.nearestPaymentDate}
              futureExtensionFees={activeLoan?.futureExtensionFees?.[0]?.fee}
              loading={pending}
            />
          </div>

          <Button
            onClick={handleGoToAccount}
            pending={pending || isAuthModeResolving}
          >
            {t('approved:go_to_account')}
          </Button>
        </Card>
        {client?.id && (
          <Card
            className={classNames([styles.card, styles.congratulation])}
            dataTest="approved-congratulation-card"
          >
            <Typography
              kind="h1"
              size="xxl"
              color="blue"
              centered
              className={styles.title}
              fontWeight={700}
              dataTest="approved-congratulation-title"
            >
              {t('approved:congratulation.title')}
            </Typography>

            <Typography
              className={styles.description}
              centered
              size="s2"
              dataTest="approved-congratulation-description"
            >
              {t('approved:congratulation.text')}
            </Typography>

            <div className={styles.approvedBtn}>
              <Button
                pending={pending}
                external
                wide
                to={`${import.meta.env.VITE_TYPE_FORM}#user_id=${
                  client?.number
                }`}
                dataTest="approved-congratulation-btn"
              >
                {t('approved:congratulation.btn')}
              </Button>
            </div>
          </Card>
        )}
      </div>
    </>
  );
}
